// client/src/pages/Game/Battlefield/components/StartVortex.jsx
import React, { useEffect, useRef, useState } from 'react';
import './StartVortex.css';

/**
 * Cinématique de lancement de partie (vs IA) :
 *   1. ASPIRATION (~1.1s) : TOUT l'écran (vidéo comprise — la classe body
 *      cible .battlefield-container, parent de la vidéo ET du frame)
 *      s'enroule : rotation 1.6 tours + collapse au centre + blur, pendant
 *      qu'un canvas dessine des centaines de traînées élémentaires suivant
 *      des spirales logarithmiques (dr/dt = -a·r, dθ/dt = b/r → rotation de
 *      plus en plus rapide près du cœur, comme un siphon).
 *   2. SINGULARITÉ (~0.75s) : écran noir, le cœur s'éteint.
 *   3. ÉJECTION (~1s) : flash lumineux + explosion radiale inverse des
 *      particules ; le battlefield est « recraché » (rotation inverse,
 *      léger overshoot), puis le shi-fu-mi prend le relais.
 *   4. DISTRIBUTION (~1.5s) : chaque joueur pioche ses 4 cartes.
 *   5. onComplete → flux normal (shi-fu-mi / mulligan).
 * Clic n'importe où = passer l'animation.
 */
const PHASES = {
  SUCK: 0,      // écran aspiré dans le vortex
  BLACK: 1,     // singularité noire
  REVEAL: 2,    // éjection du battlefield
  DRAW: 3,      // pioche des 4 cartes
  DONE: 4,
};

const T_SUCK = 1100;
const T_BLACK = 750;
const T_REVEAL = 1000;
const T_DRAW = 1500;

/* Palette : les 5 éléments + une teinte arcane */
const ELEMENT_COLORS = [
  [255, 110, 50],   // feu
  [50, 180, 255],   // eau
  [120, 205, 85],   // terre
  [255, 215, 120],  // lumière
  [160, 105, 255],  // ténèbres
  [205, 165, 255],  // arcane
];

const StartVortex = ({ onComplete }) => {
  const [phase, setPhase] = useState(PHASES.SUCK);
  const timersRef = useRef([]);
  const canvasRef = useRef(null);
  const drawLayerRef = useRef(null);
  const phaseRef = useRef(PHASES.SUCK);
  const doneRef = useRef(false);

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    timersRef.current.forEach(clearTimeout);
    document.body.classList.remove('vortex-sucking', 'vortex-revealing');
    if (onComplete) onComplete();
  };

  useEffect(() => {
    const t = timersRef.current;
    t.push(setTimeout(() => setPhase(PHASES.BLACK), T_SUCK));
    t.push(setTimeout(() => setPhase(PHASES.REVEAL), T_SUCK + T_BLACK));
    t.push(setTimeout(() => setPhase(PHASES.DRAW), T_SUCK + T_BLACK + T_REVEAL));
    t.push(setTimeout(finish, T_SUCK + T_BLACK + T_REVEAL + T_DRAW));
    return () => {
      t.forEach(clearTimeout);
      document.body.classList.remove('vortex-sucking', 'vortex-revealing');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Classes body : pilotent l'aspiration/éjection du CONTAINER entier
  // (vidéo + frame), pas seulement du frame.
  useEffect(() => {
    phaseRef.current = phase;
    document.body.classList.toggle('vortex-sucking', phase === PHASES.SUCK);
    document.body.classList.toggle('vortex-revealing', phase === PHASES.REVEAL);
  }, [phase]);

  /* ── Moteur de particules (spirales logarithmiques) ────────────── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const dpr = Math.min(window.devicePixelRatio || 1, 1.75);
    let W = 0, H = 0, CX = 0, CY = 0, MAXR = 1, CORE = 40;

    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      CX = W / 2;
      CY = H / 2;
      MAXR = Math.hypot(W, H) / 2;
      CORE = Math.max(36, Math.min(W, H) * 0.07);
    };
    resize();
    window.addEventListener('resize', resize);

    const COUNT = Math.max(260, Math.min(640, Math.round((W * H) / 2300)));
    const parts = new Array(COUNT);
    for (let i = 0; i < COUNT; i++) parts[i] = { dead: true, arm: i % 5 };

    const rnd = Math.random;

    /* 5 BRAS élémentaires : chaque bras naît sur un rayon fixe (± jitter) et
       garde sa couleur. Le cisaillement θ' = b/r enroule chaque bras en
       spirale — un vrai tourbillon, pas un simple dégradé qui tourne. */
    const ARM_B = [640, 800, 960, 1120, 1280]; // vitesses tangentielles par bras

    const spawnSuck = (p) => {
      p.mode = 'suck';
      p.r = MAXR * (0.28 + rnd() * 0.82);
      p.theta = (p.arm / 5) * Math.PI * 2 + (rnd() - 0.5) * 0.55;
      p.a = 0.75 + rnd() * 0.85;            // taux de collapse radial (par s)
      p.b = ARM_B[p.arm] + rnd() * 160;     // vitesse tangentielle du bras
      p.size = 0.9 + rnd() * 2.1;
      p.color = ELEMENT_COLORS[p.arm];      // FEU / EAU / TERRE / LUMIÈRE / TÉNÈBRES
      p.dead = false;
    };

    const spawnBurst = (p) => {
      p.mode = 'burst';
      p.r = CORE * (0.4 + rnd() * 0.9);
      p.theta = rnd() * Math.PI * 2;
      p.vr0 = p.vr = MAXR * (0.9 + rnd() * 1.5); // vitesse d'éjection
      p.vt = (rnd() - 0.5) * 3.2;
      p.size = 1 + rnd() * 2.6;
      p.life = 0.9 + rnd() * 0.6;
      p.color = ELEMENT_COLORS[(rnd() * ELEMENT_COLORS.length) | 0];
      p.dead = false;
    };

    /* Traînée lumineuse : halo large + cœur brillant (faux glow bon marché) */
    const streak = (x0, y0, x1, y1, p, a) => {
      const near = 1 - Math.min(p.r / MAXR, 1);
      const w = p.size * (0.55 + 1.7 * near * near);
      const cr = p.color[0], cg = p.color[1], cb = p.color[2];
      ctx.strokeStyle = 'rgba(' + cr + ',' + cg + ',' + cb + ',' + (0.10 * a).toFixed(3) + ')';
      ctx.lineWidth = w * 3;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(' + Math.min(cr + 70, 255) + ',' + Math.min(cg + 45, 255) + ',' + Math.min(cb + 45, 255) + ',' + (0.55 * a).toFixed(3) + ')';
      ctx.lineWidth = w;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
    };

    let raf = 0;
    let last = performance.now();
    const startedAt = last;
    let revealT = -1;
    let idleSince = -1;

    const frame = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      const ph = phaseRef.current;

      // Rémanence : fondu progressif du canvas → traînées persistantes
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0,0,0,0.15)';
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'lighter';

      if (ph === PHASES.SUCK || ph === PHASES.BLACK) {
        revealT = -1;
        const warm = now - startedAt < 400;
        const dim = ph === PHASES.BLACK ? 0.30 : 1;
        for (let i = 0; i < COUNT; i++) {
          const p = parts[i];
          if (p.dead) {
            if (warm || rnd() < 0.5) spawnSuck(p);
            else continue;
          }
          const x0 = CX + p.r * Math.cos(p.theta);
          const y0 = CY + p.r * Math.sin(p.theta);
          // Spirale logarithmique : θ' = b/r (tourne plus vite près du
          // centre), r' = -a·r (collapse accéléré) — effet siphon.
          p.theta += (p.b / Math.max(p.r, 26)) * dt;
          p.r *= Math.exp(-p.a * dt);
          if (p.r < CORE * 0.5) { p.dead = true; continue; }
          const x1 = CX + p.r * Math.cos(p.theta);
          const y1 = CY + p.r * Math.sin(p.theta);
          streak(x0, y0, x1, y1, p, dim);
        }
      } else if (ph === PHASES.REVEAL) {
        if (revealT < 0) {
          revealT = 0;
          for (let i = 0; i < COUNT; i++) parts[i].dead = true;
        } else {
          revealT += dt;
        }
        // Vagues d'éjection au début de la phase
        if (revealT < 0.42) {
          for (let i = 0; i < COUNT; i++) {
            if (parts[i].dead && rnd() < 0.14) spawnBurst(parts[i]);
          }
        }
        for (let i = 0; i < COUNT; i++) {
          const p = parts[i];
          if (p.dead) continue;
          const x0 = CX + p.r * Math.cos(p.theta);
          const y0 = CY + p.r * Math.sin(p.theta);
          p.vr *= Math.exp(-2.7 * dt);           // friction : l'éjection freine
          p.r += p.vr * dt;
          p.theta += (p.vt * dt) / (1 + p.r / 260);
          p.life -= dt;
          const a = Math.max(0, Math.min(1, p.vr / p.vr0)) * Math.min(1, p.life / 0.4);
          if (p.life <= 0 || p.r > MAXR * 1.05 || a <= 0.02) { p.dead = true; continue; }
          const x1 = CX + p.r * Math.cos(p.theta);
          const y1 = CY + p.r * Math.sin(p.theta);
          streak(x0, y0, x1, y1, p, a);
        }
      } else if (idleSince < 0) {
        idleSince = now;
      }

      // Phase pioche : plus rien à dessiner → on laisse le canvas s'éteindre
      // puis on stoppe la boucle.
      if (ph === PHASES.DRAW && idleSince > 0 && now - idleSince > 700) {
        ctx.clearRect(0, 0, W, H);
        return;
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  /* ── Phase pioche : 8 dos de carte volent des decks vers les mains ── */
  useEffect(() => {
    if (phase !== PHASES.DRAW) return;
    const layer = drawLayerRef.current;
    if (!layer) return;

    const frame = document.querySelector('.battlefield-frame');
    if (!frame) return;
    const fr = frame.getBoundingClientRect();
    // ⚠ La couche est dans l'overlay PORTÉ SUR <body> (fixed = viewport).
    const lr = layer.getBoundingClientRect();

    const deckOf = (cls) => {
      const el = document.querySelector('.' + cls);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { x: r.left + r.width / 2 - lr.left, y: r.top + r.height / 2 - lr.top };
    };

    const handOf = (cls) => {
      const el = document.querySelector('.' + cls) || document.querySelector(cls);
      if (el) {
        const r = el.getBoundingClientRect();
        return { x: r.left + r.width / 2 - lr.left, y: r.top + r.height / 2 - lr.top };
      }
      return cls.includes('opponent')
        ? { x: lr.width / 2, y: lr.height * 0.12 }
        : { x: lr.width / 2, y: lr.height * 0.88 };
    };

    const sides = [
      { deck: deckOf('opponent-deck') || { x: fr.width * 0.12, y: fr.height * 0.2 }, hand: handOf('opponent-hand'), fan: -1 },
      { deck: deckOf('player-deck') || { x: fr.width * 0.88, y: fr.height * 0.8 }, hand: handOf('player-hand'), fan: 1 },
    ];

    const ghosts = [];
    sides.forEach((side, sIdx) => {
      for (let i = 0; i < 4; i++) {
        const g = document.createElement('div');
        g.className = 'vortex-draw-card';
        const w = fr.width * 0.09;
        const h = w * 1.45;
        g.style.width = w + 'px';
        g.style.height = h + 'px';
        g.style.left = (side.deck.x - w / 2) + 'px';
        g.style.top = (side.deck.y - h / 2) + 'px';
        layer.appendChild(g);

        const spread = (i - 1.5) * w * 0.75;
        const anim = g.animate(
          [
            { transform: 'translate(0,0) rotate(0deg)', opacity: 0 },
            { opacity: 1, offset: 0.15 },
            {
              transform: 'translate(' + (side.hand.x - side.deck.x + spread) + 'px,' + (side.hand.y - side.deck.y) + 'px) rotate(' + (side.fan * (i - 1.5) * 6) + 'deg)',
              opacity: 0.15,
            },
          ],
          {
            duration: 700,
            delay: 150 + sIdx * 250 + i * 90,
            easing: 'cubic-bezier(0.25, 0.9, 0.35, 1)',
            fill: 'forwards',
          }
        );
        anim.onfinish = () => g.remove();
        ghosts.push(anim);
      }
    });

    return () => ghosts.forEach((a) => { try { a.cancel(); } catch (e) { /* noop */ } });
  }, [phase]);

  const cls = phase === PHASES.SUCK ? 'vortex-suck'
    : phase === PHASES.BLACK ? 'vortex-black'
    : phase === PHASES.REVEAL ? 'vortex-reveal'
    : 'vortex-draw';

  return (
    <div className={'start-vortex-overlay ' + cls} onClick={finish}>
      {/* Traînées élémentaires (spirale) */}
      <canvas ref={canvasRef} className="vortex-canvas" />
      {/* Cœur / singularité (au-dessus des particules : elles y plongent) */}
      <div className="vortex-core" />
      {/* Flash d'éjection */}
      {phase === PHASES.REVEAL && <div className="vortex-flash" />}
      <div ref={drawLayerRef} className="vortex-draw-layer" />
      {phase === PHASES.DRAW && <div className="vortex-hint">Distribution des cartes…</div>}
      {(phase === PHASES.SUCK || phase === PHASES.REVEAL) && (
        <div className="vortex-hint subtle">Cliquez pour passer</div>
      )}
    </div>
  );
};

export default StartVortex;
